import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  CloudUpload as BulkIcon,
  Info as InfoIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const SUGGESTED_PERSONAL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'gmx.com',
  'live.com',
  'msn.com'
];

function DomainPurposeSection({ purpose, title, description, showPersonalSuggestions }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [addDialog, setAddDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, domain: null });
  const [newDomain, setNewDomain] = useState('');
  const [newReason, setNewReason] = useState('');
  const [bulkDomains, setBulkDomains] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    domain: true,
    reason: true,
    addedBy: true,
    addedOn: true,
    status: true
  });

  const fetchDomains = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/blocked-domains', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          purpose
        }
      });
      setDomains(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, purpose]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Domain is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.post('/admin/blocked-domains', {
        domain: newDomain.trim(),
        reason: newReason.trim() || undefined,
        block_purpose: purpose
      });
      toast.success(`Domain "${newDomain}" added`);
      setAddDialog(false);
      setNewDomain('');
      setNewReason('');
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add domain');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    const domainList = bulkDomains
      .split(/[\n,;]+/)
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
    if (domainList.length === 0) {
      toast.error('Please enter at least one domain');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.post('/admin/blocked-domains/bulk', {
        domains: domainList,
        block_purpose: purpose
      });
      const result = response.data.data;
      toast.success(`Added ${result.added} domains, skipped ${result.skipped} existing`);
      setBulkDialog(false);
      setBulkDomains('');
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add domains');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleDomain = async (row) => {
    try {
      if (row.is_active) {
        await api.patch(`/admin/blocked-domains/${row.id}`);
        toast.success(`Removed @${row.domain} from this list`);
      } else {
        await api.post('/admin/blocked-domains', {
          domain: row.domain,
          block_purpose: purpose
        });
        toast.success(`Re-added @${row.domain}`);
      }
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update');
    }
  };

  const handleDeleteDomain = async () => {
    if (!deleteDialog.domain) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/blocked-domains/${deleteDialog.domain.id}`);
      toast.success(`Deleted @${deleteDialog.domain.domain}`);
      setDeleteDialog({ open: false, domain: null });
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const addSuggestedPersonal = () => {
    setBulkDomains(SUGGESTED_PERSONAL_DOMAINS.join('\n'));
  };

  const deleteHint =
    purpose === 'signup'
      ? 'Users will be able to register with this domain again.'
      : 'Bulk sends may include addresses at this domain again.';
  const visibleColCount =
    (visibleCols.domain ? 1 : 0) +
    (visibleCols.reason ? 1 : 0) +
    (visibleCols.addedBy ? 1 : 0) +
    (visibleCols.addedOn ? 1 : 0) +
    (visibleCols.status ? 1 : 0) +
    1;

  return (
    <Paper sx={{ mb: 4 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<BulkIcon />} onClick={() => setBulkDialog(true)}>
              Bulk add
            </Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}>
              Add domain
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search domains..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <IconButton onClick={fetchDomains} color="primary" size="small">
          <RefreshIcon />
        </IconButton>
        <Chip label={`${totalCount} domain(s)`} size="small" variant="outlined" icon={<BlockIcon />} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon fontSize="small" />}
          onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
          type="button"
        >
          Columns
        </Button>
        <Menu
          anchorEl={colMenuAnchorEl}
          open={Boolean(colMenuAnchorEl)}
          onClose={() => setColMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {[
            ['domain', 'Domain'],
            ['reason', 'Reason'],
            ['addedBy', 'Added by'],
            ['addedOn', 'Added on'],
            ['status', 'Status']
          ].map(([key, label]) => (
            <MenuItem
              key={key}
              onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
              dense
            >
              <Checkbox size="small" checked={Boolean(visibleCols[key])} />
              <Typography variant="body2">{label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {visibleCols.domain && <TableCell>Domain</TableCell>}
              {visibleCols.reason && <TableCell>Reason</TableCell>}
              {visibleCols.addedBy && <TableCell>Added by</TableCell>}
              {visibleCols.addedOn && <TableCell>Added on</TableCell>}
              {visibleCols.status && <TableCell>Status</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : domains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColCount} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No domains in this list.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              domains.map((row) => (
                <TableRow key={row.id} hover>
                  {visibleCols.domain && <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      @{row.domain}
                    </Typography>
                  </TableCell>}
                  {visibleCols.reason && <TableCell>
                    {row.reason || (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>}
                  {visibleCols.addedBy && <TableCell>
                    {row.creator
                      ? `${row.creator.first_name} ${row.creator.last_name}`
                      : '—'}
                  </TableCell>}
                  {visibleCols.addedOn && <TableCell>{format(new Date(row.createdAt), 'MMM dd, yyyy')}</TableCell>}
                  {visibleCols.status && <TableCell>
                    {row.is_active ? (
                      <Chip label="Active" color="error" size="small" icon={<ActiveIcon />} />
                    ) : (
                      <Chip label="Inactive" size="small" />
                    )}
                  </TableCell>}
                  <TableCell align="center">
                    <Tooltip title={row.is_active ? 'Remove from list' : 'Re-add'}>
                      <IconButton size="small" onClick={() => handleToggleDomain(row)}>
                        {row.is_active ? <ActiveIcon color="success" /> : <BlockIcon color="error" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete permanently">
                      <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, domain: row })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add domain — {purpose === 'bulk_email' ? 'bulk emailing' : 'user signup'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Domain"
            placeholder="e.g. competitor.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }}
            helperText="Without the @ symbol"
          />
          <TextField fullWidth label="Reason (optional)" value={newReason} onChange={(e) => setNewReason(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDomain} disabled={actionLoading || !newDomain.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk add — {purpose === 'bulk_email' ? 'bulk emailing' : 'user signup'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
            One domain per line, or separate with commas. The @ symbol is optional.
          </Alert>
          {showPersonalSuggestions && (
            <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={addSuggestedPersonal}>
              Fill common personal domains (suggested for bulk-email exclusion only)
            </Button>
          )}
          <TextField
            fullWidth
            multiline
            rows={10}
            label="Domains"
            value={bulkDomains}
            onChange={(e) => setBulkDomains(e.target.value)}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkAdd} disabled={actionLoading || !bulkDomains.trim()}>
            Add domains
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, domain: null })}>
        <DialogTitle>Delete domain</DialogTitle>
        <DialogContent>
          {deleteDialog.domain && (
            <Alert severity="warning">
              Permanently delete <strong>@{deleteDialog.domain.domain}</strong> from this list?
              <br />
              <br />
              {deleteHint}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, domain: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteDomain} disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const DomainStatus = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
      Domain Status
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
      Manage two independent lists. Bulk-email blocks do not affect who may sign up; signup blocks do not affect bulk-mail
      suppression lists.
    </Typography>

    <DomainPurposeSection
      purpose="bulk_email"
      title="Domains blocked for bulk emailing"
      description="When the system sends bulk or campaign mail (for example from the email extractor or mailing features), it will not send to addresses at these domains—useful to avoid mailing large providers (e.g. google.com, microsoft.com) or competitors."
      showPersonalSuggestions
    />

    <Divider sx={{ my: 3 }} />

    <DomainPurposeSection
      purpose="signup"
      title="Domains blocked for user signup"
      description="Users cannot create an account with an email address at these domains. Leave this list empty to allow registration with any domain. This list can be large; use bulk add if needed."
      showPersonalSuggestions={false}
    />
  </Box>
);

export default DomainStatus;
